/**
 * 分镜检查器 - 「内容」tab
 *
 * 包含：提示词 + 角色徽标 + 物品徽标 + 场景 + 时长/衔接 + 镜头调度（折叠） + Prompt 预览（折叠）
 * 编辑后由父组件做 debounce 自动保存，本组件不再有「保存」按钮。
 */

import { useRef, useState } from 'react';
import {
  Type,
  Users,
  MapPin,
  Camera,
  ChevronDown,
  FileText,
  Package,
  Film,
  ImageIcon,
  Lightbulb,
} from 'lucide-react';
import type {
  CharacterLayoutItem,
  DirectorCharacter,
  DirectorProp,
  DirectorScene,
  DirectorShotMode,
} from '../../../services/directorApi';
import { MentionTextarea, type MentionCandidate, type MentionTextareaHandle } from '../MentionTextarea';
import { CameraStagingPanel, summarizeStaging } from '../CameraStagingPanel';
import { PromptPreviewPanel } from '../PromptPreviewPanel';
import { PromptTemplateBar } from '../../shared/PromptTemplateBar';

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100';

interface Props {
  /** 当前分镜模式：keyframe（单图 → 视频）/ frame_sequence（多图 → 视频） */
  shotMode: DirectorShotMode;
  /** 项目画面比例 */
  projectAspectRatio: '16:9' | '9:16' | '1:1' | string;
  projectCharacters: DirectorCharacter[];
  projectScenes: DirectorScene[];
  projectProps: DirectorProp[];

  /** 当前字段（受控） */
  prompt: string;
  /** 通过 prompt 内 @ 引用解析出的场景 ID（取第一个 @ 到的） */
  mentionedSceneId: string | null;
  /** 多余被 @ 到但被忽略的场景 ID 列表（用于警告） */
  mentionedSceneIdsExtra: string[];
  cameraMovement: string | null;
  motionIntensity: string | null;
  characterLayout: CharacterLayoutItem[];

  /** 合并后的提及候选（character + prop + scene） */
  mentionCandidates: MentionCandidate[];
  /** 由 prompt @ 派生的角色顺序 */
  mentionedCharacters: DirectorCharacter[];
  /** 由 prompt @ 派生的物品顺序 */
  mentionedProps: DirectorProp[];

  /** 是否禁用编辑（视频已激活） */
  videoActive: boolean;

  /** setter */
  setPrompt: (v: string) => void;
  setCharacterLayout: (v: CharacterLayoutItem[]) => void;
  setCameraMovement: (v: string | null) => void;
  setMotionIntensity: (v: string | null) => void;
}

export function ShotContentTab(props: Props) {
  const {
    shotMode,
    projectAspectRatio,
    projectCharacters,
    projectScenes,
    projectProps,
    prompt,
    mentionedSceneId,
    mentionedSceneIdsExtra,
    cameraMovement,
    motionIntensity,
    characterLayout,
    mentionCandidates,
    mentionedCharacters,
    mentionedProps,
    videoActive,
    setPrompt,
    setCharacterLayout,
    setCameraMovement,
    setMotionIntensity,
  } = props;

  const isFrameSequence = shotMode === 'frame_sequence';

  const [stagingOpen, setStagingOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  // 命令式 ref，给"模板快速插入"使用（插入到光标位置）
  const promptRef = useRef<MentionTextareaHandle | null>(null);

  const mentionedScene = mentionedSceneId
    ? projectScenes.find((s) => s.id === mentionedSceneId) ?? null
    : null;

  const stagingHasValue = characterLayout.length > 0 || !!cameraMovement || !!motionIntensity;
  const stagingSummary = summarizeStaging({
    layoutCount: characterLayout.length,
    cameraMovement,
    motionIntensity,
  });

  // 物品徽标编号紧接角色之后
  const propStartIndex = mentionedCharacters.length + 1;

  const placeholderHint = (() => {
    const haveAny =
      projectCharacters.length > 0 ||
      projectProps.length > 0 ||
      projectScenes.length > 0;
    if (!haveAny) return '可去左栏新建角色 / 物品 / 场景后用 @ 引用';
    return '输入 @ 引用角色 / 物品 / 场景';
  })();

  return (
    <div className="space-y-5">
      {/* 模式副标题：说明本 tab 的内容影响范围 */}
      {isFrameSequence ? (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] leading-4 text-amber-800">
          <Film className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span>
            <b className="font-semibold">仅影响视频效果</b>（动作 / 节奏 / 过渡）。每张帧的画面在
            「多图 → 视频」tab 内单独描述。
          </span>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-2xl border border-sky-200 bg-sky-50/70 px-3 py-2 text-[11px] leading-4 text-sky-800">
          <ImageIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-500" />
          <span>
            这里的内容会<b className="font-semibold">同时影响</b>：① 关键帧图片生成 · ② 最终视频效果。
          </span>
        </div>
      )}

      {/* 提示词 */}
      <div className="space-y-1.5">
        <label className={SECTION_LABEL}>
          <Type className="h-3 w-3" /> 提示词
        </label>
        <MentionTextarea
          ref={promptRef}
          value={prompt}
          onChange={setPrompt}
          candidates={mentionCandidates}
          rows={5}
          maxLength={2000}
          disableMention={isFrameSequence}
          placeholder={
            isFrameSequence
              ? '描述动作 / 节奏 / 过渡（如：先放下杯子，接着起身，动作连贯）'
              : '描述本分镜的画面，输入 @ 引用角色 / 物品 / 场景'
          }
          className={FIELD}
        />
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-400">
            {isFrameSequence ? '描述「怎么动」，画面由帧序列承载' : placeholderHint}
          </span>
          <span className="text-slate-400">{prompt.length}/2000</span>
        </div>

        {/* 模板快速插入栏（景别 / 镜头 / 节奏 / 情绪 / 转场 / 光影） */}
        <PromptTemplateBar
          onInsert={(text) => promptRef.current?.insertText(text)}
          disabled={videoActive}
        />

        {/* 写作建议（仅多图模式，默认折叠） */}
        {isFrameSequence && (
          <div className="rounded-2xl border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setTipsOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
                写作建议（多图模式）
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-slate-400 transition ${tipsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {tipsOpen && (
              <div className="space-y-3 border-t border-slate-100 px-3 py-3 text-[12px] leading-5 text-slate-600">
                <p>
                  一条原则：<b className="text-slate-800">写动作、节奏、过渡和情绪，不要重复描述帧里已经有的画面</b>
                  （角色长相、构图、场景）。视频模型已经"看到"了你的帧。
                </p>
                <div>
                  <p className="mb-1 font-semibold text-slate-700">推荐写：</p>
                  <ul className="list-disc space-y-0.5 pl-4 text-slate-500">
                    <li>时序连接词：先 / 接着 / 然后 / 最后</li>
                    <li>过渡方式：平滑过渡、无缝衔接、短暂停顿后</li>
                    <li>节奏速度：由慢到快、先快后缓、节奏舒缓</li>
                    <li>物理反馈：头发随风飘动、衣摆自然摆动</li>
                  </ul>
                </div>
                <div>
                  <p className="mb-1 font-semibold text-slate-700">避免写：</p>
                  <ul className="list-disc space-y-0.5 pl-4 text-slate-500">
                    <li>角色外观：黑长发、白衬衫、戴眼镜</li>
                    <li>静态构图：人物在画面左侧，背景是窗户</li>
                    <li>场景设定：在咖啡馆里、黄昏时分</li>
                  </ul>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                    示例
                  </p>
                  <p className="text-slate-600">
                    先放下咖啡杯，接着起身走向门口，动作连贯流畅，杯子放下时有轻微声响感。
                  </p>
                </div>
                <p className="text-[11px] text-slate-400">
                  镜头运动（推 / 拉 / 摇等）也写在这里即可，例如「镜头缓慢推近」「左摇过场」。
                </p>
              </div>
            )}
          </div>
        )}

        {/* 徽标区：角色（sky）+ 物品（amber）——仅单图模式 */}
        {!isFrameSequence &&
          (mentionedCharacters.length > 0 || mentionedProps.length > 0) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {mentionedCharacters.map((c, idx) => (
                <span
                  key={`char-${c.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[11px] font-medium text-sky-700"
                  title={`参考图${idx + 1}：${c.name}`}
                >
                  <span className="rounded-full bg-sky-500 px-1.5 text-[10px] font-semibold text-white">
                    {idx + 1}
                  </span>
                  {c.name}
                </span>
              ))}
              {mentionedProps.map((p, idx) => (
                <span
                  key={`prop-${p.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700"
                  title={`参考图${propStartIndex + idx}：${p.name}`}
                >
                  <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
                    {propStartIndex + idx}
                  </span>
                  {p.name}
                </span>
              ))}
              {mentionedScene && (
                <span
                  key={`scene-${mentionedScene.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700"
                  title={`参考图${mentionedCharacters.length + mentionedProps.length + 1}：${mentionedScene.name}`}
                >
                  <span className="rounded-full bg-emerald-500 px-1.5 text-[10px] font-semibold text-white">
                    {mentionedCharacters.length + mentionedProps.length + 1}
                  </span>
                  {mentionedScene.name}
                </span>
              )}
            </div>
          )}

        {/* 多场景警告：每分镜只能用一个场景，多余的被忽略 */}
        {!isFrameSequence && mentionedSceneIdsExtra.length > 0 && mentionedScene && (
          <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-800">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
              每分镜只能引用一个场景，已采用 <b className="font-mono">@{mentionedScene.name}</b>
              ；其余 {mentionedSceneIdsExtra.length} 个被忽略。
            </span>
          </div>
        )}
      </div>

      {/* 角色 / 物品 / 场景 引导（仅单图模式且对应资产为空时） */}
      {!isFrameSequence &&
        projectCharacters.length > 0 &&
        mentionedCharacters.length === 0 && (
          <div className="flex items-start gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>
              还没有 @ 角色。在提示词里输入 <span className="font-mono text-sky-600">@</span> 选择角色，
              定妆照会按顺序作为「参考图1 / 参考图2 / …」传给模型。
            </span>
          </div>
        )}
      {!isFrameSequence &&
        projectProps.length > 0 &&
        mentionedProps.length === 0 && (
          <div className="flex items-start gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
            <Package className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>
              还没有 @ 物品。在提示词里输入 <span className="font-mono text-amber-600">@</span> 选择物品，
              主图会紧接角色之后传给模型。
            </span>
          </div>
        )}
      {!isFrameSequence &&
        projectScenes.length > 0 &&
        !mentionedScene && (
          <div className="flex items-start gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>
              还没有 @ 场景。在提示词里输入 <span className="font-mono text-emerald-600">@</span> 选择场景，
              场景设定图会作为最后一张参考图传给模型。
            </span>
          </div>
        )}

      {/* 镜头调度（折叠）——仅单图模式；多图模式下不参与帧 / 视频生成，整块隐藏 */}
      {!isFrameSequence && (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setStagingOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
          >
            <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold text-slate-700">
              <Camera className="h-3.5 w-3.5 text-sky-500" />
              镜头调度
              {stagingHasValue && <span className="inline-flex h-1.5 w-1.5 rounded-full bg-sky-500" />}
              {!stagingOpen && stagingSummary && (
                <span className="truncate text-[11px] font-normal text-slate-500">
                  · {stagingSummary}
                </span>
              )}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-slate-400 transition ${stagingOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {stagingOpen && (
            <div className="border-t border-slate-100 px-3 py-3">
              <CameraStagingPanel
                aspectRatio={projectAspectRatio}
                mentionedCharacters={mentionedCharacters.map((c) => ({
                  id: c.id,
                  name: c.name,
                  thumbnailUrl: c.portraitThumbnailUrl ?? c.portraitUrl ?? null,
                }))}
                layout={characterLayout}
                cameraMovement={cameraMovement}
                motionIntensity={motionIntensity}
                disabled={videoActive}
                onChange={(next) => {
                  setCharacterLayout(next.layout);
                  setCameraMovement(next.cameraMovement);
                  setMotionIntensity(next.motionIntensity);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Prompt 预览（折叠） */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setPreviewOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
        >
          <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
            <FileText className="h-3.5 w-3.5 text-sky-500" />
            最终 Prompt 预览
            <span className="text-[11px] font-normal text-slate-500">· 送给模型的文本</span>
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition ${previewOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {previewOpen && (
          <div className="border-t border-slate-100 px-3 py-3">
            <PromptPreviewPanel
              userPrompt={prompt}
              videoOnly={isFrameSequence}
              characterRefs={mentionedCharacters.map((c) => ({ id: c.id, name: c.name }))}
              propRefs={mentionedProps.map((p) => ({ id: p.id, name: p.name }))}
              sceneRef={
                mentionedScene && mentionedScene.settingImageJobId
                  ? { id: mentionedScene.id, name: mentionedScene.name }
                  : null
              }
              characterLayout={characterLayout}
              cameraMovement={cameraMovement}
              motionIntensity={motionIntensity}
            />
          </div>
        )}
      </div>
    </div>
  );
}
